import React, { useEffect, useState, useRef, useMemo } from 'react';
import { useParams, useLocation, useHistory } from 'react-router-dom';
import {
  Card, Form, Row, Col, InputGroup, Button, Toast,
} from 'react-bootstrap';
import { Element, scroller } from 'react-scroll';

import '../../ProjectCreate.less';
import '../../Page.less';
import '../../../components/Form.less';

import BackBtn from '../../../components/UI/BackBtn';
import PortfolioScrollBox from '../PortfolioScrollBox';
import StatusBadge from '../../../components/project/PassportRead/StatusBadge';
import CommonInfo from '../../../components/project/PassportEdit/CommonInfo';
import Contents from '../../../components/project/PassportEdit/Contents';
import ButtonsCard from '../../../components/project/PassportEdit/ButtonsCard';
import Results from '../../../components/project/PassportEdit/Results';
import Indicators from '../../../components/project/PassportEdit/Indicators';
import Budget from '../../../components/project/PassportEdit/Budget';
import TeamMembers from '../../../components/project/PassportEdit/TeamMembers';

import useAPI from '../../../hooks/useAPI';
import useUserContext from '../../../hooks/useUserContext';
import useNavProject from '../../../hooks/useNavProject';
import useToast from '../../../hooks/useToast';
import { makeUID, stepScroll } from '../../../helpers';
import { getMngData, getPeriodsData, _default } from './helpers/passport';
import { getActionsModel } from './helpers/passportRoleStatus';
import { isAdmin, isProjectCreator } from '../../../services/rolesSvc';
import { checkDatesFull } from '../../../components/project/PassportEdit/helpers/validation';
import { status as PS } from '../../../helpers/passport';
import { getSectionLinkPart, getTypeProject, getPathType } from "../../../helpers/typeProject";

import ExpertDecisionCard from './ExpertDecisionCard';
import {getFixedPcs} from "../../../requests/exportRequests";

export default function PortfolioPassportEditPage() {
  const history = useHistory();
  let { stratId, portfolioId } = useParams();
  portfolioId = portfolioId && portfolioId.split('?')[0]
  const navProject = useNavProject();
  const formEl = useRef(null);
  const api = useAPI();
  const userCtx = useUserContext();
  const addToast = useToast();
  const [requestPending,setRequestPending] = useState(false)

  const [mngData, setMngData] = useState(_default.mngData);
  const [periods, setPeriods] = useState([]);
  const [portfolio, setPortfolio] = useState(_default.portfolio(stratId, portfolioId));
  const [validated, setValidated] = useState(false);
  const [err, setErr] = useState(false);
  const [resultMsg, setResultMsg] = useState(null);
  const [show, setShow] = useState(false);
  const [fixedPcsData,setFixedPcs] = useState([])
  const [validationFailedTeamMembers, setValidationFailedTeamMembers] = useState(false);

  const sectionLinkPart = useMemo(() => getSectionLinkPart(location.pathname), [location.pathname]);
  const typeProject = useMemo(() => getTypeProject(sectionLinkPart), [sectionLinkPart]);

  useEffect(async () => {
    const responseMngData = await getMngData(api);
    setMngData(responseMngData);
    const responsePeriods = await getPeriodsData(api);
    setPeriods(responsePeriods);
    const fetchedPortf = await getPortfolio(portfolio.id);
    const can = getActionsModel(fetchedPortf?.status, userCtx, stratId, fetchedPortf?.origins?.originPortfId);
    if (portfolioId !== 'new' && !can.Edit && !can.EditCurrent) {
      addToast.error('Недостаточно прав для редактирования портфеля');
      history.push(`/strats/${stratId}`);
      return;
    }

    const canCreateNew = isProjectCreator(userCtx, stratId);
    if (portfolioId === 'new' && !canCreateNew) {
      addToast.error('Недостаточно прав для создания портфеля');
      history.push(`/strats/${stratId}`);
    }
  }, []);

  useEffect(()=>{
    async function fetchFixedPcs(){
      const responseFixedPcsData = await getFixedPcs(api,addToast)
      setFixedPcs(responseFixedPcsData)
    }
    fetchFixedPcs()
  },[])

  const switchToReadMode = () => {
    history.go(-1);
  };

  const validateCommonData = () => {
    const isDatesValid = !checkDatesFull(portfolio.startDate, portfolio.endDate);
    const isKpisSelected = portfolio.kpis && portfolio.kpis.length;
    const validationResult = isDatesValid && isKpisSelected;

    if (!isDatesValid) { addToast.error('Проверьте сроки реализации портфеля'); }
    if (!isKpisSelected) addToast.error('Необходимо выбрать показатели программы (один или более)');
    if (!validationResult) scroller.scrollTo('CommonInfo', stepScroll);

    return validationResult;
  };

  const validateTeamMembers = () => {
    const requiredRoles = ['head', 'admin', 'employer'];
    const validTeamMembers = portfolio.users && requiredRoles.every((role) => portfolio.users.find((user) => user.role === role));
    if (!validTeamMembers) {
      addToast.error('Необходимо назначить все обязательные роли Команды управления');
      scroller.scrollTo('Participants', stepScroll);
      setValidationFailedTeamMembers(true);
    }
    return validTeamMembers;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    event.stopPropagation();
    setRequestPending(true)

    const form = event.currentTarget;

    if (form.checkValidity() === false) {
      const invalidEl = formEl.current.querySelector(':invalid');
      invalidEl.scrollIntoView({ block: 'center', behavior: 'smooth' });
    } else if (validateCommonData() && validateTeamMembers()) {
      await savePortfolio();
    }
    setValidated(true);
    setRequestPending(false)
  };

  const savePortfolio = async () => {
    const res = await api.post(`/portfolios/${portfolio.id}`, portfolio);
    if (res.id) {
      addToast.success('Портфель успешно сохранен');
      setShow(true);
      setErr(false);
      history.push(`/strats/${stratId}/portfolios/${res.id}`);
    } else {
      setResultMsg(res.error && res.error.message || 'Не удалось сохранить портфель');
      setShow(true);
      setErr(true);
    }
  };

  const getPortfolio = async (id) => {
    if (id) {
      const resPortfolio = await api.get(`/portfolios/${id}`);
      const respPortfolioStructure = await api.get(`/structure/portfolios/${id}`);

      const strategyName = respPortfolioStructure.strategy.name;

      navProject.setPortfolioTitle(respPortfolioStructure.name);
      navProject.setStratTitle(strategyName);

      const newProj = {
        ...resPortfolio,
        strategyName,
        users: resPortfolio.users.map((u) => ({ ...u, hash: makeUID(5) })),
        startDate: resPortfolio.startDate ? resPortfolio.startDate.substring(0, 10) : undefined,
        endDate: resPortfolio.endDate ? resPortfolio.endDate.substring(0, 10) : undefined,
        approvedOn: resPortfolio.approvedOn ? resPortfolio.approvedOn.substring(0, 10) : undefined,
      };

      setPortfolio(newProj);
      return newProj;
    }
    const resStrat = await api.get('/mng/strategies?short=true');
    const strategyName = portfolio.strategyId ? resStrat.find((s) => s.id === parseInt(portfolio.strategyId)).name : '';

    navProject.setPortfolioTitle('Новый портфель');
    navProject.setStratTitle(strategyName);

    const newProj = {
      ...portfolio,
      strategyName,
      strategy: portfolio.strategy ? {
        ...portfolio.strategy,
        type: typeProject
      } : {
        type: typeProject
      }
    };

    setPortfolio(newProj);
    return newProj;
  };

  const handleExpertsDecision = async ({ recommendations, protocol }) => {
    const response = await api.post(`/portfolios/${portfolioId}/expertDecision`, { isApproved: true, recommendations, protocol });
    if (!response.error) {
      setPortfolio((prev) => ({
        ...prev,
        status: response.status,
        expertGroupDecision: response.expertGroupDecision,
        expertGroupProtocol: response.expertGroupProtocol,
        commentHead: null,
        commentPortf: null,
        commentExpert: null,
      }));
      addToast.success('Реквизиты решения обновлены');
    }
  };

  const onProjFieldValueUpdate = (value, field) => {
    setPortfolio({ ...portfolio, [field]: value });
  };

  return (
    <section className="page form-page project-card-page">
      <div className="container">
        <div style={{ marginBottom: 16 }}>
          <BackBtn />
        </div>

        <PortfolioScrollBox />

        <div style={styledContainer}>
          <h4 style={styledSubHeading}>
            {portfolioId === 'new' || !portfolio.name ? 'Цифровой паспорт' : portfolio.name}
          </h4>
          {portfolio.status && <div><StatusBadge status={portfolio.status} /></div>}
        </div>

        <Form
          noValidate
          validated={validated}
          onSubmit={handleSubmit}
          ref={formEl}
        >
          <Element id="CommonInfo" name="CommonInfo">
            <CommonInfo
              data={portfolio}
              onChange={(value, field) => onProjFieldValueUpdate(value, field)}
              letters={mngData.letters}
              kpis={mngData.kpis}
              types={mngData.projTypes}
              labelsCfg={{
                numLabel: 'Номер портфеля',
                namePlaceholder: 'Введите название портфеля',
                startDateLabel: 'Дата начала портфеля',
                endDateLabel: 'Дата окончания портфеля',
                typeLabel: 'Тип проектов в портфеле',
                startDateErrorMsg: 'Дата начала реализации портфеля должна быть не ранее текущей даты',
                endDateErrorMsg: 'Дата окончания реализации портфеля должна быть не ранее текущей даты',
                parentGoalsLabel: 'Дополнительные стратегические цели, на которые влияет портфель',
                parentPoliciesLabel: 'Политики развития, на которые влияет портфель',
                partnersLabel: 'Партнеры портфеля',
              }}
              validated={validated}
            />
          </Element>
          <Element id="Contents" name="Contents">
            <Contents
              data={portfolio}
              onChange={(value, field) => onProjFieldValueUpdate(value, field)}
              labelsCfg={{
                goalPlaceholder: 'Цель описывает как изменится ситуация в результате реализации портфеля. Цель проекта должна быть кратко сформулированной.',
                descrPlaceholder: 'Опишите особенности портфеля, технологии и способы его реализации',
              }}
            />
          </Element>
          <Element id="Results" name="Results">
            <Results
              data={portfolio}
              periods={periods}
              onChange={(value, field) => onProjFieldValueUpdate(value, field)}
              letters={mngData.letters}
              resultTypes={mngData.resultTypes}
              labelsCfg={{
                cardHeader: 'Результаты портфеля',
                resultLabel: 'Описание итогового результата портфеля',
                resultPlaceholder: 'Описание итогового результата портфеля в соответствии с информацией в Программе развития',
                productDescrLabel: 'Описание продуктов портфеля',
                consumersLabel: 'Потребители результатов портфеля (продуктов)',
                consumersPlaceholder: 'Необходимо указать внутренние подразделения университета либо категории сотрудников, которые будут применять результаты портфеля. Необходимо отдельно выделить главного потребителя, т.е. подразделение на результатах которого главным образом скажется портфель.',
              }}
            />
          </Element>
          <Element id="Indicators" name="Indicators">
            <Indicators
                isPortfolio={true}
                fixedPcs={fixedPcsData}
              data={portfolio}
              periods={periods}
              onChange={(value, field) => onProjFieldValueUpdate(value, field)}
              labelsCfg={{
                placeholder: 'Измеримые критерии, которые должны быть удовлетворены, для того чтобы портфель мог считаться успешным. Показатели могут быть как количественные (например, «Количество вновь созданных совместных программ с ведущими вузами, реализация которых началась в текущем году») так и качественные (например, «Проведен анализ ключевых территорий и представлены рекомендации по продвижению»).',
              }}
            />
          </Element>
          <Element id="Budget" name="Budget">
            <Budget
              data={portfolio.plans}
              start={portfolio.startDate ? new Date(portfolio.startDate).getFullYear() : null}
              end={portfolio.endDate ? new Date(portfolio.endDate).getFullYear() : null}
              periods={periods}
              onChange={(value, field) => onProjFieldValueUpdate(value, field)}
              finSources={mngData.finSources}
            />
          </Element>
          <Element id="Participants" name="Participants">
            <TeamMembers
              data={portfolio.users}
              onChange={(value, field) => onProjFieldValueUpdate(value, 'users')}
              userRoles={mngData.userRoles}
              validationFailed={validationFailedTeamMembers}
              addRole={['participantAdmin']}
            />
          </Element>
          {[PS.expertsApproved, PS.approved].includes(portfolio.status)
            && isAdmin(userCtx)
          && (
          <ExpertDecisionCard
            className="mb-4"
            readOnly={false}
            onEdit={handleExpertsDecision}
            proj={portfolio}
          />
          )}
          <Element>
            <ButtonsCard
                isRequestPending={requestPending}
                projId={portfolio.id}
              switchToReadMode={switchToReadMode}
            />
          </Element>
        </Form>
      </div>
      <Toast
        style={{
          position: 'fixed', top: 0, right: 0, color: 'white',
        }}
        className="d-inline-block m-1"
        bg={!err ? 'info' : 'danger'}
        onClose={() => setShow(false)}
        show={show}
        delay={5000}
        autohide
      >
        <Toast.Body>
          {resultMsg}
        </Toast.Body>
      </Toast>
    </section>
  );
}

const styledSubHeading = { marginBottom: 24, lineHeight: '23px' };
const styledContainer = { display: 'flex', marginTop: 36, justifyContent: 'space-between' };
